import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { Clock, BookOpen, GraduationCap, Users, Plus, Edit3, Folder, Bell, ChevronLeft, ChevronRight, X } from 'lucide-react';
import '../styles/CalendarPage.css';
import { Link } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const categories = {
  Ders: { icon: BookOpen, key: 'ders' },
  Sınav: { icon: GraduationCap, key: 'sinav' },
  Ödev: { icon: Edit3, key: 'odev' },
  Etkinlik: { icon: Users, key: 'etkinlik' },
  Proje: { icon: Folder, key: 'proje' },
  Hatırlatma: { icon: Bell, key: 'hatirlatma' },
};

const weekDays = ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'];
const monthNames = [
  'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
  'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık',
];

const STORAGE_KEY = 'calendarEvents';
const SWIPE_MIN = 60;

// Yerel saate göre YYYY-AA-GG (toISOString UTC'ye çevirdiği için Türkiye'de bir gün kayıyordu)
const pad = (n) => String(n).padStart(2, '0');
const formatDate = (date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
const parseDate = (str) => {
  const [y, m, d] = str.split('-').map(Number);
  return new Date(y, m - 1, d);
};
const catKey = (category) => categories[category]?.key || 'hatirlatma';
const byTime = (a, b) => (a.time || '99:99').localeCompare(b.time || '99:99');
const longDate = (date) => date.toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' });

const CategoryIcon = ({ category, size = 14 }) => {
  const Icon = categories[category]?.icon;
  return Icon ? <Icon size={size} aria-hidden="true" /> : null;
};

/* ---------- Pencere (mobilde alttan açılan panel) ---------- */
const Modal = ({ title, onClose, children }) => {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') onCloseRef.current(); };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  return (
    <div className="cal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="cal-modal" role="dialog" aria-modal="true" aria-label={title}>
        <div className="cal-modal-head">
          <h3>{title}</h3>
          <button type="button" className="cal-modal-close" onClick={onClose} aria-label="Kapat"><X size={18} /></button>
        </div>
        {children}
      </div>
    </div>
  );
};

const loadEvents = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved) : {};
  } catch (error) {
    console.error('localStorage yükleme hatası:', error);
    return {};
  }
};

function CalendarPage() {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [events, setEvents] = useState(loadEvents);
  const [form, setForm] = useState(null); // null: kapalı | { mode, id, originalDate, text, time, category, date }
  const [formError, setFormError] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [hiddenCats, setHiddenCats] = useState([]);
  const [notifPermission, setNotifPermission] = useState(
    typeof window !== 'undefined' && 'Notification' in window ? Notification.permission : 'unsupported'
  );

  const eventsRef = useRef(events);
  const notifiedRef = useRef(new Set());
  const touchRef = useRef(null);
  eventsRef.current = events;

  // Kaydetme: render dışında, değişiklik olunca
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(events));
    } catch (error) {
      console.error('localStorage kaydetme hatası:', error);
      toast.error('Etkinlikler bu cihaza kaydedilemedi.', { toastId: 'storage-error' });
    }
  }, [events]);

  // Açılışta bugünün etkinlikleri tek bir bildirimde özetlenir
  useEffect(() => {
    const todayEvents = [...(eventsRef.current[formatDate(new Date())] || [])].sort(byTime);
    if (todayEvents.length === 0) return;
    const lines = todayEvents.slice(0, 4).map((e) => `• ${e.time || '--:--'} ${e.text}`);
    if (todayEvents.length > 4) lines.push(`+${todayEvents.length - 4} etkinlik daha`);
    toast.info(`Bugün ${todayEvents.length} etkinliğiniz var\n${lines.join('\n')}`, {
      toastId: 'today-summary',
      autoClose: 6000,
    });
  }, []);

  // Yaklaşan etkinlik bildirimi: dakikada bir kontrol, aynı etkinlik için yalnızca bir kez
  useEffect(() => {
    const check = () => {
      if (!('Notification' in window) || Notification.permission !== 'granted') return;
      const now = new Date();
      const today = formatDate(now);
      (eventsRef.current[today] || []).forEach((event) => {
        if (!event.time) return;
        const key = `${today}-${event.id}-${event.time}`;
        if (notifiedRef.current.has(key)) return;
        const diff = new Date(`${today}T${event.time}`) - now;
        if (diff > 0 && diff < 15 * 60 * 1000) {
          notifiedRef.current.add(key);
          try {
            new Notification(`Hatırlatma: ${event.text}`, {
              body: `Saat ${event.time} için ${event.category}`,
              icon: '/icon.png',
            });
          } catch (error) {
            console.error('Bildirim hatası:', error);
          }
        }
      });
    };
    check();
    const interval = setInterval(check, 60 * 1000);
    return () => clearInterval(interval);
  }, [notifPermission]);

  // İzin yalnızca kullanıcı isteyince sorulur (tarayıcılar kendiliğinden açılan istekleri engeller)
  const enableNotifications = async () => {
    try {
      const result = await Notification.requestPermission();
      setNotifPermission(result);
      if (result === 'granted') toast.success('Hatırlatma bildirimleri açıldı.');
      else toast.warn('Bildirim izni verilmedi. Tarayıcı ayarlarından açabilirsiniz.');
    } catch (error) {
      console.error('Bildirim izni hatası:', error);
    }
  };

  const isHidden = useCallback((event) => hiddenCats.includes(event.category), [hiddenCats]);

  const getEventsForDate = useCallback(
    (date) => (events[formatDate(date)] || []).filter((e) => !isHidden(e)).sort(byTime),
    [events, isHidden]
  );

  /* ---------- Ekleme / düzenleme / silme ---------- */
  const openAddForm = (date = selectedDate) => {
    setFormError('');
    setConfirmDelete(false);
    setForm({ mode: 'add', id: null, originalDate: null, text: '', time: '', category: 'Ders', date: formatDate(date) });
  };

  const openEditForm = (event, date) => {
    const dateStr = formatDate(date || selectedDate);
    if (date) setSelectedDate(date);
    setFormError('');
    setConfirmDelete(false);
    setForm({
      mode: 'edit',
      id: event.id,
      originalDate: dateStr,
      text: event.text,
      time: event.time || '',
      category: event.category,
      date: dateStr,
    });
  };

  const closeForm = () => {
    setForm(null);
    setFormError('');
    setConfirmDelete(false);
  };

  const updateForm = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  const saveEvent = (e) => {
    e.preventDefault();
    if (!form) return;
    if (!form.text.trim()) {
      setFormError('Etkinlik adı boş olamaz.');
      return;
    }
    if (!form.date) {
      setFormError('Lütfen bir tarih seçin.');
      return;
    }

    const event = {
      id: form.id ?? Date.now(),
      text: form.text.trim(),
      time: form.time,
      category: form.category,
    };

    setEvents((prev) => {
      const next = { ...prev };
      if (form.mode === 'edit') {
        // Tarih değiştiyse eski günden çıkar
        next[form.originalDate] = (next[form.originalDate] || []).filter((x) => x.id !== form.id);
        if (next[form.originalDate].length === 0) delete next[form.originalDate];
      }
      next[form.date] = [...(next[form.date] || []).filter((x) => x.id !== event.id), event];
      return next;
    });

    const target = parseDate(form.date);
    setSelectedDate(target);
    setCurrentMonth(target);
    toast.success(`${form.mode === 'edit' ? 'Etkinlik güncellendi' : 'Etkinlik eklendi'}: ${event.text}`, { autoClose: 2500 });
    closeForm();
  };

  const deleteEvent = () => {
    if (!form || form.mode !== 'edit') return;
    const dateStr = form.originalDate;
    const deleted = (events[dateStr] || []).find((x) => x.id === form.id);
    setEvents((prev) => {
      const next = { ...prev, [dateStr]: (prev[dateStr] || []).filter((x) => x.id !== form.id) };
      if (next[dateStr].length === 0) delete next[dateStr];
      return next;
    });
    toast.warn(`Etkinlik silindi: ${deleted?.text || 'Etkinlik'}`, { autoClose: 2500 });
    closeForm();
  };

  /* ---------- Ay görünümü ---------- */
  const days = useMemo(() => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const startOffset = firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1; // Pazartesi başlangıç

    const list = [];
    for (let i = startOffset - 1; i >= 0; i--) list.push({ date: new Date(year, month, -i), isCurrentMonth: false });
    for (let day = 1; day <= daysInMonth; day++) list.push({ date: new Date(year, month, day), isCurrentMonth: true });
    const remaining = 42 - list.length;
    for (let day = 1; day <= remaining; day++) list.push({ date: new Date(year, month + 1, day), isCurrentMonth: false });

    // Tamamen sonraki aya ait son haftaları gösterme
    while (list.length > 28 && list.slice(-7).every((d) => !d.isCurrentMonth)) list.length -= 7;
    return list;
  }, [currentMonth]);

  const todayStr = formatDate(new Date());
  const selectedStr = formatDate(selectedDate);
  const viewingThisMonth =
    currentMonth.getFullYear() === new Date().getFullYear() && currentMonth.getMonth() === new Date().getMonth();

  const navigateMonth = (direction) => {
    setCurrentMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() + direction, 1));
  };

  const goToToday = () => {
    const now = new Date();
    setCurrentMonth(now);
    setSelectedDate(now);
  };

  // Telefonda yatay kaydırarak ay değiştirme
  const onTouchStart = (e) => {
    const t = e.touches[0];
    touchRef.current = { x: t.clientX, y: t.clientY };
  };
  const onTouchEnd = (e) => {
    const start = touchRef.current;
    touchRef.current = null;
    if (!start) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - start.x;
    const dy = t.clientY - start.y;
    if (Math.abs(dx) > SWIPE_MIN && Math.abs(dx) > Math.abs(dy) * 1.5) navigateMonth(dx < 0 ? 1 : -1);
  };

  const toggleCategory = (category) =>
    setHiddenCats((prev) => (prev.includes(category) ? prev.filter((c) => c !== category) : [...prev, category]));

  const selectedEvents = getEventsForDate(selectedDate);
  const selectedIsToday = selectedStr === todayStr;

  // Önümüzdeki 7 gün
  const upcoming = useMemo(() => {
    const list = [];
    const start = new Date();
    for (let i = 0; i < 7; i++) {
      const d = new Date(start.getFullYear(), start.getMonth(), start.getDate() + i);
      (events[formatDate(d)] || [])
        .filter((e) => !hiddenCats.includes(e.category))
        .sort(byTime)
        .forEach((e) => list.push({ ...e, date: d }));
    }
    return list;
  }, [events, hiddenCats]);

  const upcomingLabel = (d) => {
    const diff = Math.round((parseDate(formatDate(d)) - parseDate(todayStr)) / 86400000);
    if (diff === 0) return 'Bugün';
    if (diff === 1) return 'Yarın';
    return d.toLocaleDateString('tr-TR', { weekday: 'short', day: 'numeric', month: 'short' });
  };

  return (
    <div className="cal-page">
      <ToastContainer limit={3} position="top-right" autoClose={4000} newestOnTop closeOnClick pauseOnHover />

      {/* Başlık */}
      <header className="cal-head">
        <div className="cal-container cal-head-inner">
          <div className="cal-head-copy">
            <h1>Etkinlik <span className="cal-mark">takvimi.</span></h1>
            <p>Ders programınızı, sınav tarihlerini ve önemli etkinlikleri düzenleyin.</p>
          </div>
          <div className="cal-head-actions">
            {notifPermission === 'default' && (
              <button type="button" className="cal-btn cal-btn-outline" onClick={enableNotifications}>
                <Bell size={16} aria-hidden="true" /> Hatırlatmaları aç
              </button>
            )}
            <button type="button" className="cal-btn cal-btn-solid cal-head-add" onClick={() => openAddForm()}>
              <Plus size={16} aria-hidden="true" /> Yeni etkinlik
            </button>
          </div>
        </div>
      </header>

      <main className="cal-container cal-layout">
        {/* Aylık görünüm */}
        <section className="cal-card cal-month" aria-label="Aylık takvim">
          <div className="cal-nav">
            <button type="button" onClick={() => navigateMonth(-1)} className="cal-nav-btn" aria-label="Önceki ay">
              <ChevronLeft size={20} />
            </button>
            <h2 aria-live="polite">{monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}</h2>
            <button type="button" onClick={() => navigateMonth(1)} className="cal-nav-btn" aria-label="Sonraki ay">
              <ChevronRight size={20} />
            </button>
          </div>

          <div className="cal-today-row">
            <button type="button" className="cal-today-btn" onClick={goToToday} disabled={viewingThisMonth && selectedStr === todayStr}>
              Bugüne git
            </button>
          </div>

          <div className="cal-weekdays" aria-hidden="true">
            {weekDays.map((day) => (
              <div key={day}>{day}</div>
            ))}
          </div>

          <div className="cal-days" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
            {days.map((dayObj) => {
              const dayStr = formatDate(dayObj.date);
              const dayEvents = getEventsForDate(dayObj.date);
              const selected = dayStr === selectedStr;

              let dayClasses = 'cal-day';
              if (!dayObj.isCurrentMonth) dayClasses += ' is-other';
              if (dayStr === todayStr) dayClasses += ' is-today';
              if (selected) dayClasses += ' is-selected';

              return (
                <div
                  key={dayStr}
                  role="button"
                  tabIndex={0}
                  aria-pressed={selected}
                  aria-label={longDate(dayObj.date) + (dayEvents.length ? `, ${dayEvents.length} etkinlik` : '')}
                  onClick={() => {
                    setSelectedDate(dayObj.date);
                    if (!dayObj.isCurrentMonth) setCurrentMonth(dayObj.date);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setSelectedDate(dayObj.date);
                      if (!dayObj.isCurrentMonth) setCurrentMonth(dayObj.date);
                    }
                  }}
                  className={dayClasses}
                >
                  <span className="cal-day-num">{dayObj.date.getDate()}</span>

                  <div className="cal-chips">
                    {dayEvents.slice(0, 2).map((event) => (
                      <button
                        type="button"
                        key={event.id}
                        className="cal-chip"
                        data-cat={catKey(event.category)}
                        onClick={(e) => {
                          e.stopPropagation();
                          openEditForm(event, dayObj.date);
                        }}
                      >
                        <CategoryIcon category={event.category} size={11} />
                        <span>{event.text}</span>
                      </button>
                    ))}
                    {dayEvents.length > 2 && <span className="cal-more">+{dayEvents.length - 2} daha</span>}
                  </div>

                  {dayEvents.length > 0 && (
                    <div className="cal-dots" aria-hidden="true">
                      {dayEvents.slice(0, 3).map((event) => (
                        <span key={event.id} data-cat={catKey(event.category)} />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        {/* Seçili gün */}
        <aside className="cal-side">
          <section className="cal-card cal-panel" aria-label="Seçili gün">
            <div className="cal-panel-head">
              <div>
                <span className="cal-panel-label">{selectedIsToday ? 'Bugün' : 'Seçili gün'}</span>
                <h3>{longDate(selectedDate)}</h3>
              </div>
              <button type="button" onClick={() => openAddForm()} className="cal-add" aria-label="Bu güne etkinlik ekle">
                <Plus size={18} />
                <span>Ekle</span>
              </button>
            </div>

            <div className="cal-events">
              {selectedEvents.length === 0 ? (
                <div className="cal-empty">
                  <p>Bu gün için etkinlik yok.</p>
                  <button type="button" className="cal-link-btn" onClick={() => openAddForm()}>Etkinlik ekle</button>
                </div>
              ) : (
                <ul>
                  {selectedEvents.map((event) => (
                    <li key={event.id}>
                      <button
                        type="button"
                        className="cal-event"
                        data-cat={catKey(event.category)}
                        onClick={() => openEditForm(event)}
                      >
                        <span className="cal-event-title">
                          <CategoryIcon category={event.category} size={15} />
                          <strong>{event.text}</strong>
                        </span>
                        <span className="cal-event-meta">
                          <span className="cal-event-time"><Clock size={13} aria-hidden="true" />{event.time || 'Saat belirtilmemiş'}</span>
                          <span className="cal-event-tag">{event.category}</span>
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </section>

          <section className="cal-card cal-upcoming" aria-label="Yaklaşan etkinlikler">
            <h4>Önümüzdeki 7 gün</h4>
            {upcoming.length === 0 ? (
              <p className="cal-muted">Yaklaşan etkinlik yok.</p>
            ) : (
              <ul>
                {upcoming.slice(0, 6).map((event) => (
                  <li key={`${formatDate(event.date)}-${event.id}`}>
                    <button
                      type="button"
                      className="cal-up-item"
                      data-cat={catKey(event.category)}
                      onClick={() => {
                        setSelectedDate(event.date);
                        setCurrentMonth(event.date);
                      }}
                    >
                      <span className="cal-up-when">{upcomingLabel(event.date)}<small>{event.time || '—'}</small></span>
                      <span className="cal-up-text">{event.text}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {upcoming.length > 6 && <p className="cal-muted cal-up-more">+{upcoming.length - 6} etkinlik daha</p>}
          </section>

          <section className="cal-card cal-legend" aria-label="Kategori filtresi">
            <h4>Kategoriler</h4>
            <div>
              {Object.entries(categories).map(([category, config]) => {
                const off = hiddenCats.includes(category);
                return (
                  <button
                    type="button"
                    key={category}
                    className={`cal-legend-item${off ? ' is-off' : ''}`}
                    data-cat={config.key}
                    aria-pressed={!off}
                    onClick={() => toggleCategory(category)}
                    title={off ? 'Göster' : 'Gizle'}
                  >
                    <CategoryIcon category={category} size={12} />
                    {category}
                  </button>
                );
              })}
            </div>
          </section>
        </aside>
      </main>

      <footer className="cal-container cal-foot">
        <Link to="/" className="cal-back">← Ana sayfaya dön</Link>
      </footer>

      {/* Mobil hızlı eylem düğmesi */}
      <button type="button" className="cal-fab" onClick={() => openAddForm()} aria-label="Yeni etkinlik ekle">
        <Plus size={20} aria-hidden="true" />
        <span>Etkinlik</span>
      </button>

      {form && (
        <Modal title={form.mode === 'edit' ? 'Etkinliği düzenle' : 'Yeni etkinlik'} onClose={closeForm}>
          <form className="cal-form" onSubmit={saveEvent} noValidate>
            <label className="cal-field">
              <span>Etkinlik adı</span>
              <input
                type="text"
                placeholder="Örn. Veri Yapıları vizesi"
                value={form.text}
                onChange={(e) => updateForm({ text: e.target.value })}
                className="cal-input"
                autoFocus
                maxLength={80}
              />
            </label>

            <div className="cal-form-row">
              <label className="cal-field">
                <span>Tarih</span>
                <input
                  type="date"
                  value={form.date}
                  onChange={(e) => updateForm({ date: e.target.value })}
                  className="cal-input"
                />
              </label>
              <label className="cal-field">
                <span>Saat <small>isteğe bağlı</small></span>
                <input
                  type="time"
                  value={form.time}
                  onChange={(e) => updateForm({ time: e.target.value })}
                  className="cal-input"
                />
              </label>
            </div>

            <fieldset className="cal-field cal-fieldset">
              <legend>Kategori</legend>
              <div className="cal-cat-picker">
                {Object.entries(categories).map(([category, config]) => (
                  <label key={category} className={`cal-cat-option${form.category === category ? ' is-active' : ''}`} data-cat={config.key}>
                    <input
                      type="radio"
                      name="event-category"
                      value={category}
                      checked={form.category === category}
                      onChange={() => updateForm({ category })}
                    />
                    <CategoryIcon category={category} size={14} />
                    {category}
                  </label>
                ))}
              </div>
            </fieldset>

            {formError && <p className="cal-form-error" role="alert">{formError}</p>}

            <div className="cal-form-actions">
              <button type="submit" className="cal-btn cal-btn-solid">
                {form.mode === 'edit' ? 'Kaydet' : 'Ekle'}
              </button>
              <button type="button" onClick={closeForm} className="cal-btn cal-btn-outline">İptal</button>
              {form.mode === 'edit' && (
                confirmDelete ? (
                  <button type="button" onClick={deleteEvent} className="cal-btn cal-btn-danger-solid">Silmeyi onayla</button>
                ) : (
                  <button type="button" onClick={() => setConfirmDelete(true)} className="cal-btn cal-btn-danger">Sil</button>
                )
              )}
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}

export default CalendarPage;